import React, { useEffect, useState, useCallback } from 'react';
import { api, type StayListItem, type StaySummary } from '../lib/api';
import { fmtDate, fmtClock } from '../lib/format';

type ViewKind = 'arrivals' | 'inhouse' | 'departures' | 'upcoming' | 'all';

interface Props {
  onSelect: (stayId: string) => void;
}

function statusBadge(s: string): { text: string; cls: string } {
  switch (s) {
    case 'CONFIRMED':   return { text: 'Confirmed',   cls: 'st-conf' };
    case 'CHECKED_IN':  return { text: 'In House',    cls: 'st-in'   };
    case 'CHECKED_OUT': return { text: 'Checked Out', cls: 'st-out'  };
    case 'CANCELLED':   return { text: 'Cancelled',   cls: 'st-cxl'  };
    case 'TENTATIVE':   return { text: 'Tentative',   cls: 'st-tent' };
    default:            return { text: s,             cls: ''        };
  }
}

function nightsBetween(arrival: string, departure: string): number {
  const a = new Date(arrival).getTime();
  const d = new Date(departure).getTime();
  return Math.max(1, Math.round((d - a) / 86400000));
}

export function StaysList({ onSelect }: Props) {
  const [view, setView] = useState<ViewKind>('arrivals');
  const [summary, setSummary] = useState<StaySummary | null>(null);
  const [items, setItems] = useState<StayListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, list] = await Promise.all([
        api.staysSummary(),
        api.staysList(view),
      ]);
      setSummary(s);
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const tabs: Array<{ key: ViewKind; label: string; count?: number }> = [
    { key: 'arrivals',   label: "Today's arrivals",   count: summary?.arrivals },
    { key: 'inhouse',    label: 'In-house',           count: summary?.inhouse },
    { key: 'departures', label: "Today's departures", count: summary?.departures },
    { key: 'upcoming',   label: 'Upcoming (14d)',     count: summary?.upcoming },
    { key: 'all',        label: 'All active' },
  ];

  return (
    <>
      <div className="reslist-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`reslist-tab ${view === t.key ? 'active' : ''}`}
            onClick={() => setView(t.key)}
          >
            {t.label}
            {typeof t.count === 'number' && (
              <span className="reslist-count">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}

      {loading ? (
        <div className="loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="reslist-empty">
          No stays match this view.
        </div>
      ) : (
        <table className="reslist-table">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Room / Bed</th>
              <th>Arrival</th>
              <th>Departure</th>
              <th>Nights</th>
              <th>Rate</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => {
              const badge = statusBadge(r.status);
              const room = r.roomDisplayName
                ? `${r.roomNumber} (${r.roomDisplayName})`
                : r.roomNumber;
              return (
                <tr key={r.id} onClick={() => onSelect(r.id)} className="reslist-row">
                  <td>
                    <strong>{r.occupantName}</strong>
                    {r.occupantPhone && <div className="reslist-sub">{r.occupantPhone}</div>}
                  </td>
                  <td>{room} · {r.bedLabel}</td>
                  <td>{fmtDate(r.arrivalDate)}</td>
                  <td>{fmtDate(r.departureDate)}</td>
                  <td style={{ textAlign: 'center' }}>{nightsBetween(r.arrivalDate, r.departureDate)}</td>
                  <td>${r.nightlyRate}/{r.rateType === 'WEEKLY' ? 'wk' : 'nt'}</td>
                  <td><span className={`detail-status ${badge.cls}`}>{badge.text}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
